import React, {
  forwardRef,
  useImperativeHandle,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { Box, Snackbar, Alert } from '@mui/material';
import PivotEngine from './pivot-core';
import { PivotProvider } from './context/PivotContext';
import itLocalization, { mergeLocalization } from './localization/it';
import enLocalization from './localization/en';

const LOCALES = { it: itLocalization, en: enLocalization };
import PivotToolbar from './components/Toolbar/PivotToolbar';
import PivotTable from './components/PivotTable/PivotTable';
import FieldList from './components/FieldList/FieldList';
import FormatDialog from './components/FormatDialog/FormatDialog';
import FilterBar from './components/FilterBar/FilterBar';

/**
 * Drop-in replacement for `<Pivot>` from @auraPivot/react-auraPivot.
 *
 * Preserves:
 *   - ref.auraPivot.getReport() / .setReport()
 *   - reportchange event prop
 *   - beforeToolbarCreated event prop receiving a `{ getTabs }` API
 *   - global.localization merging with the built-in Italian dictionary
 *   - global.options (grid.type, sorting, drillThrough, configuratorButton)
 *   - global.dataSource.data in the auraPivot `[metadata, ...rows]` shape
 *
 * Differences:
 *   - No 1 MB data limit — the engine streams rows internally and the grid
 *     virtualizes the viewport via react-virtuoso.
 *   - Built on MUI v7 so it inherits Databeasy's Inter font, pastel palette
 *     and dark-mode tokens from the ambient theme.
 */
const Pivot = forwardRef(function Pivot(props, ref) {
  const {
    width = '100%',
    height = '100%',
    toolbar = true,
    locale,
    global: globalProps,
    reportchange,
    beforeToolbarCreated,
  } = props;

  // Locale resolution:
  //   - prop not passed       → undefined (Intl APIs fall back to the
  //                              browser's default locale automatically);
  //                              the localization dictionary is picked from
  //                              `navigator.language` when possible.
  //   - prop passed (e.g. 'en', 'it-IT') → used verbatim for Intl calls and
  //                              its 2-char language code selects the
  //                              dictionary.
  const browserLocale =
    typeof navigator !== 'undefined' && navigator.language
      ? navigator.language
      : undefined;
  const effectiveLocale = locale || browserLocale;
  const dictKey = String(effectiveLocale || '')
    .toLowerCase()
    .slice(0, 2);
  const baseLocalization = LOCALES[dictKey] || enLocalization;

  const engineRef = useRef(null);
  if (engineRef.current === null) {
    engineRef.current = new PivotEngine();
  }
  const engine = engineRef.current;

  const [fieldsOpen, setFieldsOpen] = useState(false);
  const [formatOpen, setFormatOpen] = useState(false);
  const [snack, setSnack] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const rootRef = useRef(null);

  const handleToggleFullscreen = () => {
    const el = rootRef.current;
    if (!el) return;
    const doc = typeof document !== 'undefined' ? document : null;
    const fsEl =
      doc?.fullscreenElement ||
      doc?.webkitFullscreenElement ||
      doc?.msFullscreenElement;
    if (!fsEl) {
      const req =
        el.requestFullscreen ||
        el.webkitRequestFullscreen ||
        el.msRequestFullscreen;
      if (req) {
        const p = req.call(el);
        if (p && typeof p.catch === 'function') {
          p.catch((err) =>
            setSnack({
              severity: 'error',
              message: err?.message || 'Fullscreen error',
            })
          );
        }
      }
    } else {
      const exit =
        doc.exitFullscreen || doc.webkitExitFullscreen || doc.msExitFullscreen;
      if (exit) exit.call(doc);
    }
  };

  useEffect(() => {
    const handler = () => {
      const fsEl =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement;
      setIsFullscreen(!!fsEl && fsEl === rootRef.current);
    };
    document.addEventListener('fullscreenchange', handler);
    document.addEventListener('webkitfullscreenchange', handler);
    document.addEventListener('msfullscreenchange', handler);
    return () => {
      document.removeEventListener('fullscreenchange', handler);
      document.removeEventListener('webkitfullscreenchange', handler);
      document.removeEventListener('msfullscreenchange', handler);
    };
  }, []);

  // Localization: merge caller override on top of the Italian default.
  const localization = useMemo(
    () => mergeLocalization(baseLocalization, globalProps?.localization),
    [baseLocalization, globalProps?.localization]
  );

  // Push the localized month / weekday names to the engine so that the
  // DateHierarchyExpander builds captions in the active language. Must run
  // BEFORE the setData effect so the first expansion uses the right labels.
  useEffect(() => {
    engine.setDateLocalization(localization?.dates);
  }, [engine, localization?.dates]);

  // Push the full localization dictionary so the engine can emit
  // localized captions (grand-total label, measure captions).
  useEffect(() => {
    engine.setLocalization(localization);
  }, [engine, localization]);

  // Apply data / options from props whenever they change.
  useEffect(() => {
    const data = globalProps?.dataSource?.data;
    if (data) engine.setData(data);
  }, [engine, globalProps?.dataSource?.data]);

  useEffect(() => {
    if (globalProps?.options) engine.setOptions(globalProps.options);
  }, [engine, globalProps?.options]);

  // Wire the reportchange event through to the caller prop.
  useEffect(() => {
    if (typeof reportchange !== 'function') return undefined;
    const handler = () => reportchange();
    engine.on('reportchange', handler);
    return () => engine.off('reportchange', handler);
  }, [engine, reportchange]);

  // Expose the legacy ref API.
  useImperativeHandle(
    ref,
    () => ({
      auraPivot: {
        getReport: () => engine.getReport(),
        setReport: (report) => engine.setReport(report),
      },
      // Also expose the raw engine for consumers that want richer access.
      engine,
    }),
    [engine]
  );

  const handleExportExcel = async () => {
    try {
      await engine.exportExcel('pivot.xlsx');
    } catch (err) {
      setSnack({ severity: 'error', message: err?.message || 'Export error' });
    }
  };

  // Push the caller-provided locale to the engine so matrix-level code
  // (MatrixComputer, TreeBuilder) can use it for localeCompare / Intl calls.
  useEffect(() => {
    engine.setLocale(locale);
  }, [engine, locale]);

  const contextValue = useMemo(
    () => ({
      engine,
      localization,
      // Locale threaded through the context for React consumers (FilterBar,
      // DrillThroughDialog, DimensionFilterDialog …). `undefined` means
      // "defer to the browser default" — Intl APIs handle that natively.
      locale: locale || undefined,
      options: engine.getOptions(),
      // Expose the fullscreen root + flag so overlays (Dialog/Menu/Popover/
      // Snackbar/Tooltip) can re-portal inside the fullscreen element.
      // Without this, body-portaled overlays render behind the Fullscreen-API
      // top-layer.
      fullscreenRef: rootRef,
      isFullscreen,
    }),
    [engine, localization, locale, isFullscreen]
  );

  return (
    <PivotProvider value={contextValue}>
      <Box
        ref={rootRef}
        sx={(theme) => ({
          width,
          height,
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          fontFamily: theme.font?.primary || 'Inter',
          borderRadius: 2,
          overflow: 'hidden',
          border: `1px solid ${theme.palette.divider}`,
        })}
      >
        {toolbar && (
          <PivotToolbar
            beforeToolbarCreated={beforeToolbarCreated}
            onOpenFields={() => setFieldsOpen(true)}
            onOpenFormat={() => setFormatOpen(true)}
            onExportExcel={handleExportExcel}
            onToggleFullscreen={handleToggleFullscreen}
            isFullscreen={isFullscreen}
          />
        )}
        <FilterBar />
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <PivotTable />
        </Box>
        <FieldList open={fieldsOpen} onClose={() => setFieldsOpen(false)} />
        <FormatDialog open={formatOpen} onClose={() => setFormatOpen(false)} />
      </Box>
      <Snackbar
        open={!!snack}
        autoHideDuration={4000}
        onClose={() => setSnack(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        // Re-portal inside the fullscreen element when active, otherwise the
        // snackbar is hidden by the Fullscreen API top-layer.
        container={isFullscreen ? rootRef.current : undefined}
      >
        {snack ? (
          <Alert severity={snack.severity} onClose={() => setSnack(null)}>
            {snack.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </PivotProvider>
  );
});

Pivot.propTypes = {
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  toolbar: PropTypes.bool,
  locale: PropTypes.string,
  global: PropTypes.object,
  reportchange: PropTypes.func,
  beforeToolbarCreated: PropTypes.func,
};

export default Pivot;
